import React, { useEffect, useState, useMemo, useRef } from "react";
import L, { LatLngTuple, Map } from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import "leaflet.markercluster";
import { MapContainer, Marker, Popup, TileLayer, GeoJSON, useMap, ZoomControl } from "react-leaflet";
import { getCookie } from "typescript-cookie";
import { useTranslation } from "react-i18next";
import styles from "./styles_map.module.css";
import { SelectYear } from "../../../selects";
import { ResponseDataGet } from '../../../../models/responseDataGet';
import { EntTerritorialsUnitsPoligons } from '../../../api/territorialsUnitsPoligons';


interface MapProps {
  latitude: number;
  longitude: number;
  polygonsData?: ResponseDataGet<EntTerritorialsUnitsPoligons>;
  polygonsLoading?: boolean;
  cooperativesPoints?: any[];
  selectedYear: string;
  onYearChange: (value: number | null) => void;
}


const MarkersLayer = ({ points, lang, pane, clusterPane }: { points: any[]; lang: string | undefined; pane: string; clusterPane: string }) => {
  const map = useMap();
  const markerClusterRef = useRef<L.MarkerClusterGroup | null>(null);

  useEffect(() => {

    const style = document.createElement('style');
    style.textContent = `
      .leaflet-marker-icon {
        transition: all 0.3s ease;
        z-index: 1000;
      }

      .leaflet-marker-pane {
        z-index: 1000;
      }

      .leaflet-popup-pane {
        z-index: 2000;
      }

      .leaflet-popup {
        position: absolute;
        z-index: 2000;
      }

      .leaflet-popup * {
        z-index: 2000;
      }

      .marker-cluster {
        z-index: 1500;
        background: white;
        border: 3px solid #4D8E21;
        border-radius: 50%;
        text-align: center;
        color: #4D8E21;
        font-weight: bold;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 14px;
        box-shadow: 0 2px 4px rgba(0,0,0,0.2);
      }

      .marker-cluster span {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
      }

      .marker-cluster-small {
        width: 35px;
        height: 35px;
      }

      .marker-cluster-medium {
        width: 40px;
        height: 40px;
        font-size: 16px;
      }

      .marker-cluster-large {
        width: 45px;
        height: 45px;
        font-size: 18px;
      }

      .marker-cluster-custom {
        background: transparent;
      }

      .leaflet-popup * {
        z-index: 2000;
      }

      .custom-popup .leaflet-popup-content-wrapper {
        background: white;
        border-radius: 8px;
        box-shadow: 0 3px 14px rgba(0,0,0,0.2);
        position: relative;
        z-index: 2000;
      }

      .custom-popup .leaflet-popup-content {
        margin: 0;
        padding: 0;
        position: relative;
        z-index: 2000;
      }

      .custom-popup .leaflet-popup-content p {
        margin: 8px 0;
        padding: 0 12px;
        color: #333;
        position: relative;
        z-index: 2000;
      }

      .custom-popup .leaflet-popup-content p strong {
        color: #4D8E21;
        font-weight: 600;
      }

      .custom-popup .leaflet-popup-tip {
        background: white;
        z-index: 2000;
      }

      .custom-popup .leaflet-popup-close-button {
        color: #666;
        font-size: 16px;
        padding: 4px;
        height: 20px;
        width: 20px;
        right: 5px;
        top: 5px;
        z-index: 2001;
      }

      .custom-popup .leaflet-popup-close-button:hover {
        color: #333;
      }
    `;
    document.head.appendChild(style);

    if (!map) return;

    if (markerClusterRef.current) {
      markerClusterRef.current.clearLayers();
      map.removeLayer(markerClusterRef.current);
    }

    const customMarkerIcon = L.icon({
      iconUrl: "/cooperatives.svg",
      iconSize: [35, 55],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
    });

    const cluster = L.markerClusterGroup({
      maxClusterRadius: 50,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      zoomToBoundsOnClick: true,
      pane: clusterPane,
      iconCreateFunction: function (cluster) {
        const count = cluster.getChildCount();
        let size = 'small';
        let dimensions = [35, 35];

        if (count > 50) {
          size = 'large';
          dimensions = [45, 45];
        } else if (count > 10) {
          size = 'medium';
          dimensions = [40, 40];
        }

        return L.divIcon({
          html: `<div class="marker-cluster marker-cluster-${size}"><span>${count}</span></div>`,
          className: `marker-cluster-custom`,
          iconSize: L.point(dimensions[0], dimensions[1])
        });
      }
    });

    points.forEach((point) => {
      if (point.latitude && point.longitude) {
        const marker = L.marker([point.latitude, point.longitude], {
          icon: customMarkerIcon,
          pane: pane,
        });
        marker.bindPopup(
          ` <div style="padding: 10px; min-width: 250px; max-width: 300px">
          <div style="display: flex; flex-direction: column; gap: 8px">
            <div style="display: flex; gap: 8px">
              <span style="color: #7f8c8d; min-width: 120px">Наименование:</span>
              <span>${point.legalForm?.nameRu || "Не указано"}</span>
            </div>
            <div style="display: flex; gap: 8px">
              <span style="color: #7f8c8d; min-width: 120px">Вид деятельности:</span>
              <span>${point.economicActivity?.nameRu || "Не указано"}</span>
            </div>
            <div style="display: flex; gap: 8px">
              <span style="color: #7f8c8d; min-width: 120px">ФИО:</span>
              <span>${point.managerFullName || "Не указано"}</span>
            </div>
            <div style="display: flex; gap: 8px">
              <span style="color: #7f8c8d; min-width: 120px">Контакты:</span>
              <div>
                <a href="mailto:${point.email}" style="color: #3498db">${point.email || "Не указано"}</a><br />
                <a href="tel:${point.phone}" style="color: #3498db">${point.phone || "Не указано"}</a>
              </div>
            </div>
          </div>
        </div>`,
          {
            className: 'custom-popup'
          }
        );
        cluster.addLayer(marker);
      }
    });

    map.addLayer(cluster);
    markerClusterRef.current = cluster;

    return () => {
      if (markerClusterRef.current) {
        markerClusterRef.current.clearLayers();
        map.removeLayer(markerClusterRef.current);
      }
    };
  }, [map, points, pane, clusterPane]);

  return null;
};

const MapContent = ({
  latitude,
  longitude,
  polygonsData,
  polygonsLoading,
  cooperativesPoints = [],
  selectedYear,
  onYearChange
}: MapProps) => {
  const map = useMap();
  const polygonLayersRef = useRef<{ [key: string]: L.Polygon }>({});
  const { t } = useTranslation();
  const [lang, setLang] = useState<string | undefined>();

  useEffect(() => {
    const cookie = getCookie('i18next');
    setLang(cookie);
  }, []);

  useEffect(() => {
    if (!map || !polygonsData?.res) {
      console.warn('Map or polygons data not available', { map, polygonsData });
      return;
    }

    Object.values(polygonLayersRef.current).forEach(layer => {
      layer.remove();
    });
    polygonLayersRef.current = {};

    const markerPane = map.createPane('markerPane');
    const regionPane = map.createPane('regionPane');
    const areaPane = map.createPane('areaPane');
    const cityPane = map.createPane('cityPane');

    if (markerPane && regionPane && areaPane && cityPane) {
      markerPane.style.zIndex = '700';
      regionPane.style.zIndex = '650';
      areaPane.style.zIndex = '600';
      cityPane.style.zIndex = '550';
    }

    const polygonsToProcess = Array.isArray(polygonsData.res) ? polygonsData.res : [];
    const polygonsArray = polygonsToProcess.filter((polygon: EntTerritorialsUnitsPoligons) =>
      polygon.type === 'REGION' || polygon.type === 'AREA' || polygon.type === 'CITY_OF_REPUBLICAN_SIGNIFICANCE'
    ).sort((a: EntTerritorialsUnitsPoligons, b: EntTerritorialsUnitsPoligons) => {
      if (a.type === 'REGION') return 1;
      if (b.type === 'REGION') return -1;
      if (a.type === 'AREA') return 1;
      if (b.type === 'AREA') return -1;
      return 0;
    });

    polygonsArray.forEach((polygon: EntTerritorialsUnitsPoligons) => {
      try {
        if (!polygon.polygonJson) {
          console.warn('No polygonJson for polygon:', polygon);
          return;
        }

        const parsedPolygon = JSON.parse(polygon.polygonJson);
        if (!parsedPolygon || !Array.isArray(parsedPolygon) || !parsedPolygon[0]) {
          console.error(`Invalid polygon structure for ${polygon.nameRu}`);
          return;
        }

        const processCoordinates = (coords: number[][][]): LatLngTuple[][] => {
          return coords.map(ring =>
            ring.map(coord => [coord[1], coord[0]] as LatLngTuple)
          );
        };

        let coordinates: LatLngTuple[][] = [];
        if (parsedPolygon[0].geojson.type === 'MultiPolygon') {
          coordinates = parsedPolygon[0].geojson.coordinates.flatMap((polygonCoords: number[][][]) =>
            processCoordinates(polygonCoords)
          );
        } else {
          coordinates = processCoordinates(parsedPolygon[0].geojson.coordinates);
        }

        const getPolygonStyle = (type: string): L.PolylineOptions & { pane: string; fillOpacity: number; weight: number } => {
          switch (type) {
            case 'REGION':
              return {
                fillOpacity: 0.2,
                weight: 2,
                pane: 'regionPane'
              };
            case 'AREA':
              return {
                fillOpacity: 0.1,
                weight: 1,
                pane: 'areaPane'
              };
            default:
              return {
                fillOpacity: 0.05,
                weight: 1,
                pane: 'cityPane'
              };
          }
        };

        const style = getPolygonStyle(polygon.type);
        const polygonOptions: L.PolylineOptions = {
          ...style,
          color: 'rgba(77, 142, 33, 1)',
          fillColor: 'rgba(77, 142, 33, 1)',
          fillOpacity: style.fillOpacity,
          weight: style.weight,
          smoothFactor: 1
        };

        const polygonLayer = L.polygon(coordinates, polygonOptions).addTo(map);

        polygonLayer.on('mouseover', () => {
          polygonLayer.setStyle({
            fillOpacity: style.fillOpacity * 2,
            weight: style.weight + 1
          });
        });

        polygonLayer.on('mouseout', () => {
          polygonLayer.setStyle({
            fillOpacity: style.fillOpacity,
            weight: style.weight
          });
        });

        if (polygon.id != null) {
          polygonLayersRef.current[polygon.id.toString()] = polygonLayer;
        }
      } catch (error) {
        console.error(`Error processing polygon for ${polygon.nameRu}:`, error);
      }
    });

    if (Object.values(polygonLayersRef.current).length > 0) {
      const group = L.featureGroup(Object.values(polygonLayersRef.current));
      map.fitBounds(group.getBounds());
    } else {
      console.warn('No valid polygons were created');
    }
  }, [map, polygonsData]);

  return (
    <>
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      <MarkersLayer
        points={cooperativesPoints}
        lang={lang}
        pane="markerPane"
        clusterPane="markerPane"
      />
    </>
  );
};

export const InteractiveMapCooperative: React.FC<MapProps> = (props) => {
  return (
    <div className={styles.map_container}>
      {/* <div className={styles.yearSelector}>
        <SelectYear value={Number(props.selectedYear)} onChange={props.onYearChange} />
      </div> */}
      <MapContainer
        center={[props.latitude, props.longitude]}
        zoom={7}
        style={{ height: "100%", width: "100%" }}
        zoomControl={false}
      >
        <ZoomControl position="bottomright" />
        <MapContent {...props} />
      </MapContainer>
    </div>
  );
};
